import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { DocumentViewer } from '../components/document-viewer';
import {
    buildFindHints,
    buildHintsForDocument,
    verifyKindLabel
} from '../components/document-viewer/locate/find-hints';
import type { DocumentVerifyKind } from '../components/document-viewer/locate/find-hints';
import {
    closeDocumentViewerPopout,
    isDocumentViewerPopoutOpen,
    syncDocumentViewerPopout
} from '../components/document-viewer/document-viewer-popout';
import { buildSearchLabPdf, downloadBlob } from './generate-test-pdf';
import { createSwProfile, formatDate, SwProfile } from './star-wars-profile';
import { getTemplateById, LAB_TEMPLATES, LabTemplateDef, TemplateVariant } from './templates';
import PublishWorkflowFrame, { PublishPlaylistItem, PublishShell } from './publish-workflow-frame';
import {
    CUSTOM_FIXTURES,
    CustomFixtureField,
    buildCustomFindHints,
    getCustomFixtureById
} from './custom-fixtures';
import {
    FIXTURE_FORMATS,
    FIXTURE_ORIENTATIONS,
    FIXTURE_QUALITIES,
    FIXTURE_TEMPLATES,
    FixtureFormat,
    FixtureOrientation,
    FixtureQuality,
    FixtureTemplateId,
    buildFixtureRecipe,
    buildRecipeFindHints,
    textPdfFilename
} from './fixture-recipe';
import {
    materializeFixtureBlob,
    paintFixtureTemplate,
    applyDirtyEffects,
    applyOrientation,
    encodeRasterBlob
} from './generate-raster-fixture';
import './document-find-lab.css';

type LabMode = 'editor' | 'publish';
type OpenSource = 'pdf' | 'images' | 'upload';

const OPEN_SOURCE_KEY = 'dfl-open-source';

const LWR_TABS: Array<{ id: string; label: string; templateId: string; verifyKind: DocumentVerifyKind }> = [
    { id: 'tax', label: 'Tax Document', templateId: 'tax', verifyKind: 'tax' },
    { id: 'w2', label: 'W-2', templateId: 'w2', verifyKind: 'w2' },
    { id: 'paystub', label: 'Pay Stub', templateId: 'paystub', verifyKind: 'paystub' },
    { id: 'social_security', label: 'Social Security', templateId: 'social_security', verifyKind: 'social_security' },
    { id: 'affidavit', label: 'Self Employment Affidavit', templateId: 'self_employed', verifyKind: 'affidavit' }
];

const EXPENSE_DEFAULTS = [
    { templateId: 'lodging', expenseTypeName: 'Lodging' },
    { templateId: 'flights', expenseTypeName: 'Flight' },
    { templateId: 'meals', expenseTypeName: 'Meals' }
];

const IMAGE_EXPENSE_PACK: FixtureTemplateId[] = ['thermal_receipt', 'hotel_folio', 'line_item'];

function readOpenSource(): OpenSource {
    try {
        const v = sessionStorage.getItem(OPEN_SOURCE_KEY);
        if (v === 'pdf' || v === 'images' || v === 'upload') {
            return v;
        }
    } catch { }
    return 'pdf';
}

const DocumentFindLab = () => {
    const managedUrlsRef = useRef<string[]>([]);
    const blobRef = useRef<Blob | null>(null);

    const [seed, setSeed] = useState(42);
    const [profile, setProfile] = useState<SwProfile>(() => createSwProfile(42));
    const [templateId, setTemplateId] = useState(LAB_TEMPLATES[0].id);
    const [variant, setVariant] = useState<TemplateVariant>('standard');
    const [viewerUrl, setViewerUrl] = useState('');
    const [downloadName, setDownloadName] = useState('document.pdf');
    const [status, setStatus] = useState('Open a document to begin.');
    const [hasDoc, setHasDoc] = useState(false);

    const [labMode, setLabMode] = useState<LabMode>('editor');
    const [publishShell, setPublishShell] = useState<PublishShell>('lwr');
    const [lwrTabId, setLwrTabId] = useState('tax');
    const [lwrItems, setLwrItems] = useState<PublishPlaylistItem[]>([]);
    const [expenseItems, setExpenseItems] = useState<PublishPlaylistItem[]>([]);
    const [expenseIndex, setExpenseIndex] = useState(0);
    const [publishNote, setPublishNote] = useState('');
    const [activeCustomId, setActiveCustomId] = useState<string | null>(null);
    const [factoryFields, setFactoryFields] = useState<CustomFixtureField[] | null>(null);
    const [imageReady, setImageReady] = useState(false);
    const [factoryTemplateId, setFactoryTemplateId] = useState<FixtureTemplateId>('thermal_receipt');
    const [factoryFormat, setFactoryFormat] = useState<FixtureFormat>('jpeg');
    const [factoryQuality, setFactoryQuality] = useState<FixtureQuality>('clean');
    const [factoryOrientation, setFactoryOrientation] = useState<FixtureOrientation>(0);
    const [factoryBusy, setFactoryBusy] = useState(false);
    const [factoryPreviewUrl, setFactoryPreviewUrl] = useState('');
    const [openSource, setOpenSource] = useState<OpenSource>(() => readOpenSource());
    const [generateOpen, setGenerateOpen] = useState(false);
    const [queueOpen, setQueueOpen] = useState(false);

    const template = useMemo(() => getTemplateById(templateId) || LAB_TEMPLATES[0], [templateId]);
    const activeCustom = useMemo(
        () => (activeCustomId ? getCustomFixtureById(activeCustomId) : undefined),
        [activeCustomId]
    );
    const activeLwrTab = useMemo(
        () => LWR_TABS.find(t => t.id === lwrTabId) || LWR_TABS[0],
        [lwrTabId]
    );
    const findHints = useMemo(() => {
        if (labMode === 'publish' && publishShell === 'lwr') {
            return buildHintsForDocument(activeLwrTab.verifyKind, profile);
        }
        if (labMode === 'publish' && publishShell === 'expenses') {
            return buildHintsForDocument('expense', profile);
        }
        if (activeCustom) {
            return buildCustomFindHints(activeCustom);
        }
        if (factoryFields && factoryFields.length) {
            return buildRecipeFindHints(factoryFields);
        }
        return buildFindHints(profile);
    }, [labMode, publishShell, activeLwrTab, activeCustom, factoryFields, profile]);
    const verifyLabel = labMode === 'publish'
        ? (publishShell === 'lwr' ? verifyKindLabel(activeLwrTab.verifyKind) : 'Expense')
        : undefined;
    const autoSelectRequired = labMode === 'publish';
    const canAddToQueue = !!(viewerUrl && (activeCustom || imageReady || factoryFields || hasDoc));
    const queueCount = lwrItems.length + expenseItems.length;

    const groups = useMemo(() => {
        const map: { [k: string]: LabTemplateDef[] } = {};
        for (let i = 0; i < LAB_TEMPLATES.length; i++) {
            const t = LAB_TEMPLATES[i];
            if (!map[t.group]) {
                map[t.group] = [];
            }
            map[t.group].push(t);
        }
        return Object.keys(map).map(g => ({ group: g, items: map[g] }));
    }, []);

    const trackUrl = useCallback((url: string) => {
        managedUrlsRef.current.push(url);
        return url;
    }, []);

    const revokeAll = useCallback(() => {
        for (let i = 0; i < managedUrlsRef.current.length; i++) {
            try { URL.revokeObjectURL(managedUrlsRef.current[i]); } catch { }
        }
        managedUrlsRef.current = [];
        blobRef.current = null;
        setHasDoc(false);
        setViewerUrl('');
        setLwrItems([]);
        setExpenseItems([]);
        setExpenseIndex(0);
        setActiveCustomId(null);
        setFactoryFields(null);
        setImageReady(false);
        closeDocumentViewerPopout();
    }, []);

    useEffect(() => () => {
        revokeAll();
        closeDocumentViewerPopout();
    }, [revokeAll]);

    useEffect(() => {
        const next = createSwProfile(seed);
        setProfile(next);
        revokeAll();
        setStatus('Character set. Open a document.');
        setLabMode('editor');
        setPublishNote('');
    }, [seed, revokeAll]);

    useEffect(() => {
        const t = getTemplateById(templateId);
        if (!t) {
            return;
        }
        if (t.variants.indexOf(variant) < 0) {
            setVariant(t.variants[0]);
        }
    }, [templateId, variant]);

    useEffect(() => {
        try {
            sessionStorage.setItem(OPEN_SOURCE_KEY, openSource);
        } catch { }
    }, [openSource]);

    useEffect(() => {
        let cancelled = false;
        let objectUrl = '';
        const run = async () => {
            const painted = paintFixtureTemplate(factoryTemplateId, profile);
            const dirtied = applyDirtyEffects(painted.canvas, factoryQuality, profile.seed);
            const oriented = applyOrientation(dirtied, factoryOrientation);
            if (factoryFormat === 'jpeg' || factoryFormat === 'pdf-image') {
                const blob = await encodeRasterBlob(oriented, 'jpeg', factoryQuality);
                if (cancelled) {
                    return;
                }
                objectUrl = URL.createObjectURL(blob);
                setFactoryPreviewUrl(objectUrl);
                return;
            }
            if (cancelled) {
                return;
            }
            setFactoryPreviewUrl(oriented.toDataURL('image/png'));
        };
        run().catch(() => {
            if (!cancelled) {
                setFactoryPreviewUrl('');
            }
        });
        return () => {
            cancelled = true;
            if (objectUrl) {
                try { URL.revokeObjectURL(objectUrl); } catch { }
            }
        };
    }, [factoryTemplateId, factoryQuality, factoryOrientation, factoryFormat, profile]);

    const makeItemFromTemplate = useCallback((
        tid: string,
        p: SwProfile,
        v: TemplateVariant,
        extras?: Partial<PublishPlaylistItem>
    ): PublishPlaylistItem | null => {
        const t = getTemplateById(tid);
        if (!t) {
            return null;
        }
        const pages = t.build(p, v);
        const blob = buildSearchLabPdf(pages);
        const url = trackUrl(URL.createObjectURL(blob));
        return {
            id: (extras && extras.id) || (tid + '-' + Date.now() + '-' + Math.floor(Math.random() * 1000)),
            label: (extras && extras.label) || t.label,
            url,
            amount: extras && extras.amount != null ? extras.amount : p.receiptAmount,
            dateLabel: (extras && extras.dateLabel) || formatDate(p.receiptDate || ''),
            expenseTypeName: extras && extras.expenseTypeName
        };
    }, [trackUrl]);

    const applyBlob = (blob: Blob, filename: string, message: string, fields?: CustomFixtureField[] | null) => {
        const url = trackUrl(URL.createObjectURL(blob));
        blobRef.current = blob;
        setActiveCustomId(null);
        setFactoryFields(fields || null);
        const lower = (filename || '').toLowerCase();
        setImageReady(/\.(png|jpe?g|gif|webp|bmp)$/i.test(lower) || (blob.type || '').indexOf('image/') === 0);
        setViewerUrl(url);
        setDownloadName(filename);
        setHasDoc(true);
        setStatus(message);
        return url;
    };

    const loadCustomFixture = (id: string) => {
        const fixture = getCustomFixtureById(id);
        if (!fixture) {
            setStatus('Image fixture not found.');
            return;
        }
        setActiveCustomId(fixture.id);
        setFactoryFields(null);
        setImageReady(fixture.kind === 'image');
        blobRef.current = null;
        setViewerUrl(fixture.src);
        setDownloadName(fixture.filename);
        setHasDoc(true);
        setStatus('Opened ' + fixture.label);
    };

    const generate = () => {
        setActiveCustomId(null);
        setFactoryFields(null);
        setImageReady(false);
        const t = template;
        const pages = t.build(profile, variant);
        const blob = buildSearchLabPdf(pages);
        const name = textPdfFilename(profile.seed, t.id, variant);
        applyBlob(blob, name, `Opened ${t.label} (${pages.length} page` + (pages.length === 1 ? '' : 's') + `).`);
    };

    const runFactory = async (openInViewer: boolean, alsoDownload: boolean) => {
        if (factoryBusy) {
            return;
        }
        setFactoryBusy(true);
        try {
            const recipe = buildFixtureRecipe(profile, factoryTemplateId, factoryFormat, factoryQuality, factoryOrientation);
            const blob = await materializeFixtureBlob(recipe);
            if (alsoDownload) {
                downloadBlob(blob, recipe.filename);
            }
            if (openInViewer) {
                applyBlob(
                    blob,
                    recipe.filename,
                    `Opened generated ${recipe.label}`,
                    recipe.fields
                );
            } else if (alsoDownload) {
                setStatus('Downloaded ' + recipe.filename);
            }
        } catch (err) {
            setStatus('Generate failed.');
            toast.error('Generate failed');
        } finally {
            setFactoryBusy(false);
        }
    };

    const downloadFactoryPack = async () => {
        if (factoryBusy) {
            return;
        }
        setFactoryBusy(true);
        try {
            const templates: FixtureTemplateId[] = ['thermal_receipt', 'hotel_folio', 'paystub', 'tax', 'line_item'];
            for (let i = 0; i < templates.length; i++) {
                const recipe = buildFixtureRecipe(profile, templates[i], factoryFormat, factoryQuality, factoryOrientation);
                const blob = await materializeFixtureBlob(recipe);
                downloadBlob(blob, recipe.filename);
                await new Promise(r => setTimeout(r, 250));
            }
            setStatus('Downloaded generate pack (' + templates.length + ' files).');
        } catch (err) {
            setStatus('Generate pack failed.');
            toast.error('Generate pack failed');
        } finally {
            setFactoryBusy(false);
        }
    };

    const buildImageExpensePlaylist = async () => {
        if (factoryBusy) {
            return;
        }
        setFactoryBusy(true);
        try {
            const items: PublishPlaylistItem[] = [];
            for (let i = 0; i < IMAGE_EXPENSE_PACK.length; i++) {
                const tid = IMAGE_EXPENSE_PACK[i];
                const recipe = buildFixtureRecipe(profile, tid, 'jpeg', factoryQuality, factoryOrientation);
                const blob = await materializeFixtureBlob(recipe);
                const url = trackUrl(URL.createObjectURL(blob));
                items.push({
                    id: recipe.filename + '-' + Date.now() + '-' + i,
                    label: recipe.label,
                    url,
                    amount: recipe.amount,
                    dateLabel: recipe.dateLabel,
                    expenseTypeName: recipe.expenseTypeName === 'Income' ? 'Other' : recipe.expenseTypeName
                });
            }
            setExpenseItems(items);
            setExpenseIndex(0);
            setQueueOpen(true);
            if (items[0]) {
                setActiveCustomId(null);
                const firstRecipe = buildFixtureRecipe(profile, IMAGE_EXPENSE_PACK[0], 'jpeg', factoryQuality, factoryOrientation);
                setFactoryFields(firstRecipe.fields);
                setImageReady(true);
                blobRef.current = null;
                setViewerUrl(items[0].url);
                setDownloadName(items[0].label + '.jpg');
                setHasDoc(true);
            }
            setStatus('Publish queue: SW image expenses (' + items.length + ').');
        } catch (err) {
            setStatus('SW image expense queue failed.');
            toast.error('SW image expense queue failed');
        } finally {
            setFactoryBusy(false);
        }
    };

    const addCurrentToPlaylist = () => {
        if (!viewerUrl) {
            setStatus('Open a document first.');
            return;
        }
        if (activeCustom) {
            setExpenseItems(prev => prev.concat([{
                id: activeCustom.id + '-' + Date.now(),
                label: activeCustom.label,
                url: viewerUrl,
                amount: activeCustom.amount,
                dateLabel: activeCustom.dateLabel,
                expenseTypeName: activeCustom.expenseTypeName || 'Other'
            }]));
            setQueueOpen(true);
            setStatus('Added to expense queue: ' + activeCustom.label);
            return;
        }
        if (imageReady || factoryFields) {
            setExpenseItems(prev => prev.concat([{
                id: 'img-' + Date.now(),
                label: downloadName || 'Image',
                url: viewerUrl,
                amount: profile.receiptAmount,
                dateLabel: formatDate(profile.receiptDate || ''),
                expenseTypeName: 'Other'
            }]));
            setQueueOpen(true);
            setStatus('Added to expense queue: ' + (downloadName || 'image'));
            return;
        }
        const expenseMeta = EXPENSE_DEFAULTS.find(e => e.templateId === templateId);
        const item: PublishPlaylistItem = {
            id: 'cur-' + Date.now(),
            label: template.label,
            url: viewerUrl,
            amount: profile.receiptAmount,
            dateLabel: formatDate(profile.receiptDate || ''),
            expenseTypeName: expenseMeta ? expenseMeta.expenseTypeName : (template.group === 'Income' ? undefined : template.label)
        };
        if (!item.expenseTypeName) {
            setStatus('This file is income-type. Use Build LWR for the Publish queue.');
            setQueueOpen(true);
            return;
        }
        setExpenseItems(prev => prev.concat([item]));
        setQueueOpen(true);
        setStatus('Added to expense queue: ' + item.expenseTypeName);
    };

    const loadAndAddCustomFixture = (id: string) => {
        const fixture = getCustomFixtureById(id);
        if (!fixture) {
            return;
        }
        setActiveCustomId(fixture.id);
        setFactoryFields(null);
        setImageReady(fixture.kind === 'image');
        blobRef.current = null;
        setViewerUrl(fixture.src);
        setDownloadName(fixture.filename);
        setHasDoc(true);
        setExpenseItems(prev => prev.concat([{
            id: fixture.id + '-' + Date.now(),
            label: fixture.label,
            url: fixture.src,
            amount: fixture.amount,
            dateLabel: fixture.dateLabel,
            expenseTypeName: fixture.expenseTypeName || 'Other'
        }]));
        setQueueOpen(true);
        setStatus('Opened and queued: ' + fixture.label);
    };

    const buildExpensePlaylist = () => {
        const items: PublishPlaylistItem[] = [];
        for (let i = 0; i < EXPENSE_DEFAULTS.length; i++) {
            const def = EXPENSE_DEFAULTS[i];
            const item = makeItemFromTemplate(def.templateId, profile, 'standard', {
                expenseTypeName: def.expenseTypeName,
                amount: (profile.receiptAmount || 100) + i * 37.5,
                dateLabel: formatDate(profile.receiptDate || '')
            });
            if (item) {
                items.push(item);
            }
        }
        setExpenseItems(items);
        setExpenseIndex(0);
        setQueueOpen(true);
        setStatus('Expense queue ready (' + items.length + ').');
        return items;
    };

    const buildLwrDocs = useCallback(() => {
        const next: PublishPlaylistItem[] = [];
        for (let i = 0; i < LWR_TABS.length; i++) {
            const tab = LWR_TABS[i];
            const item = makeItemFromTemplate(tab.templateId, profile, 'standard', {
                id: tab.id,
                label: tab.label
            });
            if (item) {
                next.push(item);
            }
        }
        setLwrItems(next);
        setQueueOpen(true);
        return next;
    }, [makeItemFromTemplate, profile]);

    const enterPublish = (shell: PublishShell) => {
        try {
            setPublishShell(shell);
            setPublishNote('');
            if (shell === 'expenses') {
                if (expenseItems.length === 0) {
                    buildExpensePlaylist();
                }
                setExpenseIndex(0);
            } else {
                let nextLwr = lwrItems;
                if (nextLwr.length === 0) {
                    nextLwr = buildLwrDocs();
                }
                setLwrTabId(nextLwr[0] ? nextLwr[0].id : 'tax');
            }
            setLabMode('publish');
        } catch (err) {
            setLabMode('editor');
            setStatus('Publish failed to build documents.');
            toast.error('Publish failed to build documents.');
        }
    };

    const exitPublish = () => {
        setLabMode('editor');
        setPublishNote('');
        closeDocumentViewerPopout();
    };

    const onDownload = () => {
        if (!blobRef.current) {
            setStatus('Nothing to download (blob URL / committed fixture).');
            return;
        }
        downloadBlob(blobRef.current, downloadName);
        setStatus('Downloaded ' + downloadName);
    };

    const onUpload = (file: File | null) => {
        if (!file) {
            return;
        }
        applyBlob(file, file.name, `Opened ${file.name}.`);
    };

    const reshuffle = () => {
        setSeed(s => s + 1 + Math.floor(Math.random() * 17));
    };

    const publishItems = publishShell === 'lwr' ? lwrItems : expenseItems;

    const publishCurrentIndex = publishShell === 'lwr'
        ? Math.max(0, lwrItems.findIndex(p => p.id === lwrTabId))
        : Math.min(expenseIndex, Math.max(expenseItems.length - 1, 0));

    const publishTitle = publishShell === 'lwr'
        ? (LWR_TABS.find(t => t.id === lwrTabId)?.label || 'Document')
        : 'Receipts';

    const popOutState = useMemo(() => {
        const item = publishItems[publishCurrentIndex];
        if (!item) {
            return null;
        }
        return {
            url: item.url,
            title: publishTitle,
            showLocate: true,
            allowOcr: true,
            allowRotate: true,
            autoRunOcr: true,
            fileName: item.label,
            findHints,
            verifyLabel,
            autoSelectRequired: true,
            documentRef: {
                docType: 0,
                lookupId: 0,
                label: publishTitle,
                fileIndex: publishCurrentIndex,
                totalFiles: Math.max(publishItems.length, 1)
            }
        };
    }, [publishItems, publishCurrentIndex, publishTitle, findHints, verifyLabel]);

    const onPopOut = () => {
        if (!popOutState) {
            return;
        }
        syncDocumentViewerPopout(popOutState);
    };

    useEffect(() => {
        if (labMode !== 'publish' || !popOutState) {
            return;
        }
        if (isDocumentViewerPopoutOpen()) {
            syncDocumentViewerPopout(popOutState);
        }
    }, [labMode, popOutState]);

    const onApprove = () => {
        toast.success('Simulated Approve');
        setPublishNote('Simulated Approve');
        if (publishShell === 'expenses' && expenseIndex < expenseItems.length - 1) {
            setExpenseIndex(i => i + 1);
        }
    };

    const onRequestReview = () => {
        toast.info('Simulated Request Review');
        setPublishNote('Simulated Request Review');
        if (publishShell === 'expenses' && expenseIndex < expenseItems.length - 1) {
            setExpenseIndex(i => i + 1);
        }
    };

    return (
        <div className="dflPage">
            <div className="dflHeader">
                <div>
                    <h1 className="dflTitle">Document Find Lab</h1>
                    <p className="dflSub">Star Wars sample docs for Find / OCR / Publish testing.</p>
                </div>
                <div className="dflHeaderActions">
                    <div className="dflModeToggle">
                        <button
                            type="button"
                            className={`dflModeBtn${labMode === 'editor' ? ' dflModeBtnOn' : ''}`}
                            onClick={() => {
                                setLabMode('editor');
                                setPublishNote('');
                                closeDocumentViewerPopout();
                            }}
                        >
                            Editor
                        </button>
                        <button
                            type="button"
                            className={`dflModeBtn${labMode === 'publish' && publishShell === 'lwr' ? ' dflModeBtnOn' : ''}`}
                            onClick={() => enterPublish('lwr')}
                        >
                            Publish LWR
                        </button>
                        <button
                            type="button"
                            className={`dflModeBtn${labMode === 'publish' && publishShell === 'expenses' ? ' dflModeBtnOn' : ''}`}
                            onClick={() => enterPublish('expenses')}
                        >
                            Publish Expenses
                        </button>
                    </div>
                    {labMode === 'publish' && (
                        <button
                            type="button"
                            className="dflBtnPrimary"
                            onClick={() => {
                                setLabMode('editor');
                                setPublishNote('');
                                closeDocumentViewerPopout();
                            }}
                        >
                            Exit Publish
                        </button>
                    )}
                    <div className="dflHeaderMeta">{profile.name} · {profile.alias}</div>
                </div>
            </div>

            {labMode === 'editor' ? (
                <div className="dflLayout">
                    <aside className="dflSetup">
                        <section className="dflCard dflCardCompact">
                            <div className="dflCardTitle">Character</div>
                            <div className="dflHint">{profile.name} · {profile.alias} · {profile.planet}</div>
                            <div className="dflRow">
                                <label className="dflField">
                                    <span>Seed</span>
                                    <input
                                        type="number"
                                        value={seed}
                                        onChange={e => setSeed(parseInt(e.target.value, 10) || 0)}
                                    />
                                </label>
                                <button type="button" className="dflBtn" onClick={reshuffle}>New</button>
                            </div>
                        </section>

                        <section className="dflCard">
                            <div className="dflCardTitle">Open</div>
                            <div className="dflSourceTabs">
                                <button
                                    type="button"
                                    className={`dflSourceTab${openSource === 'pdf' ? ' dflSourceTabOn' : ''}`}
                                    onClick={() => setOpenSource('pdf')}
                                >
                                    PDF
                                </button>
                                <button
                                    type="button"
                                    className={`dflSourceTab${openSource === 'images' ? ' dflSourceTabOn' : ''}`}
                                    onClick={() => setOpenSource('images')}
                                >
                                    Images
                                </button>
                                <button
                                    type="button"
                                    className={`dflSourceTab${openSource === 'upload' ? ' dflSourceTabOn' : ''}`}
                                    onClick={() => setOpenSource('upload')}
                                >
                                    Upload
                                </button>
                            </div>

                            {openSource === 'pdf' && (
                                <div>
                                    <div className="dflTemplateList">
                                        {groups.map(g => (
                                            <div key={g.group} className="dflTemplateGroup">
                                                <div className="dflTemplateGroupTitle">{g.group}</div>
                                                {g.items.map(item => (
                                                    <button
                                                        key={item.id}
                                                        type="button"
                                                        className={`dflTemplateRow${templateId === item.id ? ' dflTemplateRowOn' : ''}`}
                                                        onClick={() => setTemplateId(item.id)}
                                                    >
                                                        {item.label}
                                                    </button>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                    {template.variants.length > 1 && (
                                        <label className="dflField" style={{ marginTop: 8 }}>
                                            <span>Variant</span>
                                            <select
                                                className="dflSelect"
                                                value={variant}
                                                onChange={e => setVariant(e.target.value as TemplateVariant)}
                                            >
                                                {template.variants.map(v => (
                                                    <option key={v} value={v}>{v}</option>
                                                ))}
                                            </select>
                                        </label>
                                    )}
                                    <div className="dflRow" style={{ marginTop: 8 }}>
                                        <button type="button" className="dflBtnPrimary" onClick={generate}>Open PDF</button>
                                    </div>
                                </div>
                            )}

                            {openSource === 'images' && (
                                <div className="dflTemplateList" style={{ maxHeight: 280 }}>
                                    {CUSTOM_FIXTURES.map(function (fixture) {
                                        const selected = activeCustomId === fixture.id;
                                        return (
                                            <div key={fixture.id} className="dflCustomFixtureBlock">
                                                <button
                                                    type="button"
                                                    className={selected ? 'dflTemplateRow dflTemplateRowOn' : 'dflTemplateRow'}
                                                    onClick={function () { loadCustomFixture(fixture.id); }}
                                                >
                                                    {fixture.label}
                                                </button>
                                                <div className="dflRow" style={{ marginBottom: 0 }}>
                                                    <button
                                                        type="button"
                                                        className="dflBtnPrimary"
                                                        onClick={function () { loadCustomFixture(fixture.id); }}
                                                    >
                                                        Open
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="dflBtn"
                                                        onClick={function () { loadAndAddCustomFixture(fixture.id); }}
                                                    >
                                                        Open + queue
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {openSource === 'upload' && (
                                <label className="dflUpload">
                                    <span>PDF · PNG · JPEG</span>
                                    <input
                                        type="file"
                                        accept="application/pdf,.pdf,image/png,image/jpeg,image/jpg,.png,.jpg,.jpeg"
                                        onChange={e => onUpload(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                                    />
                                </label>
                            )}

                            <div className="dflSubPanel">
                                <button
                                    type="button"
                                    className={`dflCollapseToggle${generateOpen ? ' dflCollapseToggleOpen' : ''}`}
                                    onClick={() => setGenerateOpen(v => !v)}
                                    aria-expanded={generateOpen}
                                >
                                    <span>Need a file? Generate</span>
                                    <span aria-hidden="true">{generateOpen ? '▾' : '▸'}</span>
                                </button>
                                {generateOpen && (
                                    <div className="dflCollapseBody">
                                        <div className="dflFactoryGrid">
                                            <label className="dflField">
                                                <span>Template</span>
                                                <select
                                                    className="dflSelect"
                                                    value={factoryTemplateId}
                                                    onChange={e => setFactoryTemplateId(e.target.value as FixtureTemplateId)}
                                                >
                                                    {FIXTURE_TEMPLATES.map(t => (
                                                        <option key={t.id} value={t.id}>{t.label}</option>
                                                    ))}
                                                </select>
                                            </label>
                                            <label className="dflField">
                                                <span>Format</span>
                                                <select
                                                    className="dflSelect"
                                                    value={factoryFormat}
                                                    onChange={e => setFactoryFormat(e.target.value as FixtureFormat)}
                                                >
                                                    {FIXTURE_FORMATS.map(t => (
                                                        <option key={t.id} value={t.id}>{t.label}</option>
                                                    ))}
                                                </select>
                                            </label>
                                            <label className="dflField">
                                                <span>Quality</span>
                                                <select
                                                    className="dflSelect"
                                                    value={factoryQuality}
                                                    onChange={e => setFactoryQuality(e.target.value as FixtureQuality)}
                                                >
                                                    {FIXTURE_QUALITIES.map(t => (
                                                        <option key={t.id} value={t.id}>{t.label}</option>
                                                    ))}
                                                </select>
                                            </label>
                                            <label className="dflField">
                                                <span>Orientation</span>
                                                <select
                                                    className="dflSelect"
                                                    value={factoryOrientation}
                                                    onChange={e => setFactoryOrientation(parseInt(e.target.value, 10) as FixtureOrientation)}
                                                >
                                                    {FIXTURE_ORIENTATIONS.map(t => (
                                                        <option key={t.id} value={t.id}>{t.label}</option>
                                                    ))}
                                                </select>
                                            </label>
                                        </div>
                                        {factoryPreviewUrl ? (
                                            <img className="dflFactoryPreview" src={factoryPreviewUrl} alt="" />
                                        ) : null}
                                        <div className="dflRow" style={{ marginTop: 8 }}>
                                            <button
                                                type="button"
                                                className="dflBtnPrimary"
                                                disabled={factoryBusy}
                                                onClick={() => runFactory(true, false)}
                                            >
                                                Open in viewer
                                            </button>
                                        </div>
                                        <div className="dflLinkRow">
                                            <button
                                                type="button"
                                                className="dflLinkBtn"
                                                disabled={factoryBusy}
                                                onClick={() => runFactory(true, true)}
                                            >
                                                Open + download
                                            </button>
                                            <button
                                                type="button"
                                                className="dflLinkBtn"
                                                disabled={factoryBusy}
                                                onClick={() => runFactory(false, true)}
                                            >
                                                Download only
                                            </button>
                                            <button
                                                type="button"
                                                className="dflLinkBtn"
                                                disabled={factoryBusy}
                                                onClick={downloadFactoryPack}
                                            >
                                                Download pack
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </section>

                        <section className="dflCard dflCardCompact">
                            <div className="dflCardTitle">This file</div>
                            {hasDoc ? (
                                <div className="dflActiveFile">
                                    <div className="dflActiveFileName" title={downloadName}>{downloadName}</div>
                                    <div className="dflActiveFileActions">
                                        <button
                                            type="button"
                                            className="dflBtn"
                                            disabled={!blobRef.current}
                                            onClick={onDownload}
                                        >
                                            ↓
                                        </button>
                                        <button
                                            type="button"
                                            className="dflBtn"
                                            disabled={!canAddToQueue}
                                            onClick={addCurrentToPlaylist}
                                        >
                                            + Queue
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="dflHint">Nothing open yet.</div>
                            )}
                            <div className="dflStatus">{status}</div>
                        </section>

                        <section className="dflCard dflCardCompact">
                            <button
                                type="button"
                                className={`dflCollapseToggle${queueOpen ? ' dflCollapseToggleOpen' : ''}`}
                                onClick={() => setQueueOpen(v => !v)}
                                aria-expanded={queueOpen}
                            >
                                <span>Publish queue · LWR {lwrItems.length} · Exp {expenseItems.length}</span>
                                <span aria-hidden="true">{queueOpen ? '▾' : '▸'}</span>
                            </button>
                            {queueOpen && (
                                <div className="dflCollapseBody">
                                    <div className="dflHint">Multi-file sets for Publish. Empty queues auto-build when you enter Publish.</div>
                                    <div className="dflRow">
                                        <button
                                            type="button"
                                            className="dflBtn"
                                            onClick={() => {
                                                buildLwrDocs();
                                                setStatus('LWR queue ready.');
                                            }}
                                        >
                                            Build LWR
                                        </button>
                                        <button type="button" className="dflBtn" onClick={buildExpensePlaylist}>
                                            Build PDF expenses
                                        </button>
                                    </div>
                                    <div className="dflRow">
                                        <button
                                            type="button"
                                            className="dflBtn"
                                            disabled={factoryBusy}
                                            onClick={buildImageExpensePlaylist}
                                        >
                                            Build SW image expenses
                                        </button>
                                    </div>
                                    {lwrItems.length > 0 && (
                                        <div className="dflPlaylist">
                                            <div className="dflCardTitle">LWR ({lwrItems.length})</div>
                                            {lwrItems.map(p => (
                                                <div key={p.id} className="dflPlaylistRow">
                                                    <span>{p.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {expenseItems.length > 0 && (
                                        <div className="dflPlaylist">
                                            <div className="dflCardTitle">Expenses ({expenseItems.length})</div>
                                            {expenseItems.map((p, idx) => (
                                                <div key={p.id} className="dflPlaylistRow">
                                                    <span>{p.expenseTypeName || p.label}</span>
                                                    <button
                                                        type="button"
                                                        className="dflLinkBtn"
                                                        onClick={() => setExpenseItems(prev => prev.filter((_, i) => i !== idx))}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {queueCount === 0 && (
                                        <div className="dflHint">Queue empty — Publish will build a default set.</div>
                                    )}
                                </div>
                            )}
                        </section>
                    </aside>

                    <main className="dflViewer">
                        {viewerUrl ? (
                            <DocumentViewer
                                url={viewerUrl}
                                findHints={findHints}
                                height="100%"
                                showOcrStub={false}
                                showLocate={true}
                                allowOcr={true}
                                allowRotate={true}
                                autoRunOcr={true}
                                fileName={downloadName}
                                onOrientedSave={(blob, name) => {
                                    applyBlob(blob, name, 'Saved oriented file · ' + name, factoryFields);
                                    downloadBlob(blob, name);
                                }}
                            />
                        ) : (
                            <div className="dflEmpty">Open a PDF, image, upload, or generate a test file.</div>
                        )}
                    </main>
                </div>
            ) : (
                <div className="dflPublishHost">
                    <PublishWorkflowFrame
                        shell={publishShell}
                        title={publishTitle}
                        packetLabel={
                            publishShell === 'lwr'
                                ? `LWR · ${profile.name}`
                                : `Expenses · ${profile.name}`
                        }
                        lwrTabs={LWR_TABS.map(t => ({ id: t.id, label: t.label }))}
                        activeLwrTabId={lwrTabId}
                        onLwrTabChange={id => setLwrTabId(id)}
                        items={publishItems}
                        currentIndex={publishCurrentIndex >= 0 ? publishCurrentIndex : 0}
                        onIndexChange={idx => {
                            if (publishShell === 'lwr') {
                                const tab = LWR_TABS[idx];
                                if (tab) {
                                    setLwrTabId(tab.id);
                                }
                            } else {
                                setExpenseIndex(idx);
                            }
                        }}
                        onPopOut={onPopOut}
                        onClose={exitPublish}
                        onApprove={onApprove}
                        onRequestReview={onRequestReview}
                        statusNote={publishNote}
                        findHints={findHints}
                        allowOcr={true}
                        verifyLabel={verifyLabel}
                        autoSelectRequired={autoSelectRequired}
                        onOrientedReplace={(index, blob, name) => {
                            const url = trackUrl(URL.createObjectURL(blob));
                            if (publishShell === 'lwr') {
                                setLwrItems(prev => prev.map((it, i) => i === index ? { ...it, url, label: name } : it));
                            } else {
                                setExpenseItems(prev => prev.map((it, i) => i === index ? { ...it, url, label: name } : it));
                            }
                            downloadBlob(blob, name);
                            setPublishNote('Saved oriented · ' + name);
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default DocumentFindLab;
